import { useIntersectionObserver } from '@uidotdev/usehooks'
import React, { useEffect, useRef, useState } from 'react'
import { useWindowSize } from 'react-use'

import ScrollHelpText from '@/components/datatable/components/ScrollHelpText'
import VirtualScrollbar from '@/components/datatable/components/VirtualScrollbar'
import { Table } from '@/components/ui/table'
import LoadingOverlay, {
  LoadingOverlayProps,
} from '@/components/widgets/LoadingOverlay'
import { useIsMinWidthSm } from '@/hooks/useIsMinWidthSm'
import { isElementOverflowingHorizontally } from '@/lib/helpers/helperFunctions'
import { cn } from '@/lib/utils'

type Props = {
  tableContainerProps?: React.HTMLAttributes<HTMLDivElement>
  tableProps?: React.ComponentProps<typeof Table>
  loadingOverlayProps?: Partial<LoadingOverlayProps>
  isLoading?: boolean
  children: React.ReactNode
  showScrollHint?: boolean
}

const DataTable: React.FC<Props> = ({
  tableContainerProps,
  tableProps,
  loadingOverlayProps,
  isLoading = false,
  children,
  showScrollHint = false,
}) => {
  const [ref, intersectionObserverEntry] = useIntersectionObserver({
    threshold: 0,
    root: null,
  })
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement | null>(null)
  const [isTableReady, setIsTableReady] = useState(false)
  const isMinWidthSm = useIsMinWidthSm()
  const isInView = !!intersectionObserverEntry?.isIntersecting
  const shouldShowVirtualScrollbar = isInView && isMinWidthSm && isTableReady
  const { width: windowWidth } = useWindowSize()

  const [isOverflowingHorizontally, setIsOverflowingHorizontally] =
    useState(false)

  useEffect(() => {
    const tableContainer = tableContainerRef.current
    const table = tableRef.current

    const isOverflowing = !!(
      tableContainer &&
      isElementOverflowingHorizontally(tableContainer) &&
      table
    )

    if (table) {
      setIsOverflowingHorizontally(isOverflowing)
    }
  }, [windowWidth])

  return (
    <div ref={ref}>
      {showScrollHint && <ScrollHelpText className="mb-2" />}

      <div
        {...tableContainerProps}
        data-slot="table-container"
        data-is-overflowing-horizontally={isOverflowingHorizontally}
        className={cn(
          `sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:after:w-px,
          relative w-full overflow-x-auto overflow-y-auto rounded-lg border
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:sticky
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:right-0
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:z-2
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:before:absolute
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:before:top-0
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:before:bottom-0
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:before:left-0
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:before:w-px
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:before:border
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-end]:before:border-gray-200/60
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:sticky
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:left-0
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:z-2
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:after:absolute
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:after:top-0
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:after:right-0
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:after:bottom-0
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:after:border
          sm:data-[is-overflowing-horizontally=true]:[&_.sticky-column-start]:after:border-gray-200/60`,
          tableProps?.darkMode ? '' : 'bg-white',
          tableContainerProps?.className,
        )}
        ref={tableContainerRef}
      >
        <LoadingOverlay isActive={isLoading} {...loadingOverlayProps}>
          {shouldShowVirtualScrollbar && (
            <VirtualScrollbar
              key={windowWidth}
              elementContainerRef={tableContainerRef}
              elementRef={tableRef}
            />
          )}

          <Table
            {...tableProps}
            ref={(ref) => {
              tableRef.current = ref
              setIsTableReady(true)
            }}
            className={tableProps?.className}
            style={{ ...tableProps?.style }}
          >
            {children}
          </Table>
        </LoadingOverlay>
      </div>
    </div>
  )
}

export default DataTable
